// DEMO ONLY. The full-screen chat cases from the Figma design, opened with `?demo=<id>`. They
// render from these modules alone: never in provider state, session storage, the API or the
// conversation list. The Figma frames live in `seeds/`, one module per build group, as the page
// response blocks a live answer would carry. Delete `seeds/` and this module, and its import in
// OrbitFullPage, to remove them.
import { ANSWER_DEMO_CHATS } from './seeds/answerSeeds';
import { HANDOFF_DEMO_CHATS } from './seeds/handoffSeeds';
import { IOrbitPageDemoChat, seedTurn } from './seeds/seedTurn';
import { ORBIT_PAGE_SHOWCASE } from './seeds/showcaseSeed';
import { SUPPORT_DEMO_CHATS } from './seeds/supportSeeds';

export type { IOrbitPageDemoChat } from './seeds/seedTurn';
export { ORBIT_PAGE_SHOWCASE, ORBIT_PAGE_SHOWCASE_ID } from './seeds/showcaseSeed';

export const ORBIT_PAGE_DEMO_CHATS: IOrbitPageDemoChat[] = [
  ...ANSWER_DEMO_CHATS,
  ...SUPPORT_DEMO_CHATS,
  ...HANDOFF_DEMO_CHATS,
  ORBIT_PAGE_SHOWCASE,
  {
    // Not a Figma frame: exercises the generic chart path with a bar and a line chart.
    id: 'generic-charts',
    title: 'Chart types (generic blocks)',
    chips: ['Screen: Customer Insights', 'Period: Last 6 Months'],
    turns: [
      seedTurn('How did redemptions and active users move over the last six months?', [
        {
          type: 'panel',
          heading: 'GOVERNED ANALYSIS',
          tone: 'info',
          blocks: [
            {
              type: 'metric_tiles',
              tiles: [
                { label: 'Redemptions', value: '18,420', caption: 'last 6 months' },
                { label: 'Active users', value: '2,378', tone: 'positive', caption: '+6.2%' },
                { label: 'Redemption rate', value: '41%' }
              ]
            },
            {
              type: 'chart',
              title: 'Redemptions by month',
              chart: {
                kind: 'bar',
                categories: ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'],
                series: [{ name: 'Redemptions', values: [2600, 2900, 3100, 2800, 3400, 3620] }],
                format: 'count'
              }
            },
            {
              type: 'chart',
              title: 'Active and transacting users',
              chart: {
                kind: 'line',
                categories: ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'],
                series: [
                  { name: 'Active', values: [2100, 2180, 2240, 2210, 2300, 2378] },
                  { name: 'Transacting', values: [1500, 1560, 1610, 1580, 1690, 1740] }
                ],
                format: 'count'
              }
            },
            {
              type: 'table',
              title: 'Top campaigns',
              columns: [
                { key: 'name', label: 'Campaign', kind: 'text' },
                { key: 'redemptions', label: 'Redemptions', kind: 'number' },
                { key: 'rate', label: 'Rate', kind: 'percent' }
              ],
              rows: [
                { name: 'Weekend Dining Instant Cashback', redemptions: 5120, rate: 46 },
                { name: 'Apex Salary Saver Quest', redemptions: 3980, rate: 39 }
              ]
            },
            { type: 'confidence', level: 'medium', score: 68 }
          ]
        }
      ])
    ]
  },
  {
    // Not a Figma frame: shows the FR §4.24 governed states, §4.25 freshness and §4.10 scope.
    id: 'governed-states',
    title: 'Governed states',
    chips: ['Screen: System & Support', 'Period: Current'],
    turns: [
      seedTurn('Show me campaign performance, our SLA plan and my open support tickets.', [
        {
          type: 'text',
          text: 'I answered the parts you can access. One part is restricted and one source is unavailable.'
        },
        {
          type: 'metric_tiles',
          state: 'stale',
          as_of: '2026-10-12T08:00:00+08:00',
          scope: { tenant: 'Acme Bank', text: 'Campaign Performance, last 30 days' },
          tiles: [
            { label: 'Engagement rate', value: '68.4%' },
            { label: 'Active campaigns', value: '42' }
          ]
        },
        {
          type: 'key_value',
          title: 'SLA plan',
          state: 'permission_restricted',
          scope: { tenant: 'Acme Bank', text: 'SLA Plan Details' },
          rows: []
        },
        {
          type: 'table',
          title: 'Open support tickets',
          state: 'upstream_error',
          columns: [{ key: 'id', label: 'Ticket ID', kind: 'id' }],
          rows: []
        },
        {
          type: 'metric_tiles',
          state: 'valid_zero',
          as_of: '2026-10-15T09:30:00+08:00',
          tiles: [{ label: 'Failed executions', value: '0' }]
        },
        {
          type: 'bullet_list',
          title: 'Not subscribed capabilities',
          state: 'entitlement_unavailable',
          items: []
        },
        {
          type: 'chart',
          title: 'Weekly redemptions',
          state: 'not_configured',
          chart: { kind: 'bar', categories: [], series: [], format: 'count' }
        }
      ]),
      seedTurn('Open ticket 48213.', [
        {
          type: 'support_ticket_detail',
          intro: 'Here are the details for ticket 48213.',
          state: 'jira_pending',
          as_of: '2026-10-15T09:30:00+08:00',
          ticket: {
            id: '48213',
            title: 'Rewards API returns errors',
            priority: 'P2',
            status: 'open',
            opened_at: '2026-10-14T11:02:00+08:00',
            updated_at: '2026-10-15T09:30:00+08:00',
            description: 'Rewards API calls return 5xx errors for this tenant.'
          }
        }
      ])
    ]
  },
  {
    // FR §4.28: a reopened conversation re-authorized by BE on load; one block is now restricted.
    id: 'history-restricted',
    title: 'Reopened chat (access changed)',
    chips: ['Screen: Business Overview', 'Period: Current'],
    turns: [
      seedTurn('When does our contract renew, and what is the ACV?', [
        {
          type: 'key_value',
          title: 'Contract renewal',
          period_applies: false,
          as_of: '2026-10-15T09:30:00+08:00',
          rows: [
            { label: 'Renewal date', value: '31 Dec 2026' },
            { label: 'Days to renewal', value: '77' }
          ]
        },
        {
          type: 'key_value',
          title: 'Annual contract value',
          state: 'permission_restricted',
          rows: []
        }
      ])
    ]
  }
];

export const findOrbitPageDemoChat = (id: string | null): IOrbitPageDemoChat | undefined =>
  id === null ? undefined : ORBIT_PAGE_DEMO_CHATS.find(demo => demo.id === id);
